import { Suspense, useEffect, useState } from 'react'
import { Outlet } from 'react-router'
import { AppFrameShell } from 'src/components/AppFrameShell'
import { RootRuntime } from 'src/components/RootShell'
import { useMatches } from 'src/utils/router/route'
import { useResolvedSsrShell } from 'src/utils/router/ssrShell'

export const handle = { name: 'app-layout' }

/**
 * Prevent hydration mismatch when child routes set clientLoader.hydrate = true
 * without a server loader. Without this, renderFallback bubbles up to root
 * where the default HydrateFallback produces a different DOM tree than the
 * server-rendered App component. Returning null here matches the server output
 * (ClientOnly renders nothing during SSR).
 */
export function HydrateFallback() {
  return null
}

function hasMatchedHandleName(
  matches: ReturnType<typeof useMatches>,
  handleName: string,
) {
  return matches.some(
    match =>
      (match.handle as { name?: string } | undefined)?.name === handleName,
  )
}

export default function AppRoute() {
  const matches = useMatches()
  const [hasHydrated, setHasHydrated] = useState(false)
  const shouldRenderSSRLeaf = hasMatchedHandleName(matches, 'ssr-probe')
  // See `src/utils/router/ssrShell.ts`. When the route uses
  // `ssrShellExperiment`, control variant returns `false` and the
  // route falls back to the `<RootRuntime>` CSR path under
  // `<AppFrameShell>` — equivalent to the Stage 1 layout behaviour.
  const shouldRenderSSRRouteShell = useResolvedSsrShell(matches)

  useEffect(() => {
    setHasHydrated(true)
  }, [])

  const shouldBypassRootRuntime =
    shouldRenderSSRLeaf || (shouldRenderSSRRouteShell && !hasHydrated)

  return (
    <AppFrameShell>
      {shouldBypassRootRuntime ? (
        <Outlet />
      ) : (
        <Suspense fallback={shouldRenderSSRRouteShell ? <Outlet /> : null}>
          <RootRuntime>
            <Outlet />
          </RootRuntime>
        </Suspense>
      )}
    </AppFrameShell>
  )
}
